import { ReactNode, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import {
  GameData,
  getCurrencyIcon,
  makeFloat,
  useApiContext,
  useGetFullBalance,
  useGetShortBalance
} from 'react-easyrocket';
import { Trans, useTranslation } from 'react-i18next';
import { useModalContext } from 'src/hooks/useModalContext';
import { FIFTEEN_SECONDS, FIVE_SECONDS } from 'src/constants';
import { useRoute } from 'src/router/hooks';

export interface IModalContent {
  content: string;
  haveAdditionalContent?: boolean;
  additionalContent?: string;
}

type NotificationKey = 'wagering' | 'maxBet' | 'deposit' | 'noWagering';

export type GameNotificationMessage = {
  id: NotificationKey;
  content: ReactNode;
  haveAdditionalContent?: boolean;
  additionalContent?: string;
};

type NotificationConfig = {
  action: (messages: GameNotificationMessage[]) => void;
  enabled: boolean;
};

export interface IModalContent {
  content: string;
  haveAdditionalContent?: boolean;
  additionalContent?: string;
}

export function useGameModals(game: GameData | undefined) {
  const { isAuthenticated } = useApiContext();
  const { t } = useTranslation();
  const { demo, gameId } = useParams();
  const { openModal, closeModal } = useModalContext();
  const depositModalTimeout = useRef<NodeJS.Timeout | null>(null);
  const { bonusAccountCasino, isSuccess: isFullBalanceReady } = useGetFullBalance({
    enabled: Boolean(game)
  });
  const { ROUTE } = useRoute();
  // Commented on task FE-9019
  // const { freeSpins, isSuccess: isFreeSpinsReady } = useGetFreeSpins();
  const isGameReady = game !== undefined;
  const { isSuccess: isShortBalanceReady } = useGetShortBalance({ refetchInterval: FIVE_SECONDS });
  const isDemo = Boolean(demo);
  const delay = isDemo ? FIFTEEN_SECONDS : FIVE_SECONDS;

  /** CONFIG */
  const notificationsConfig: Record<NotificationKey, NotificationConfig> = {
    deposit: {
      enabled: false,
      action: () => (depositModalTimeout.current = setTimeout(() => openModal('DEPOSIT'), delay))
    },
    maxBet: {
      enabled: false,
      action: (messages) => {
        messages.push({
          id: 'maxBet',
          content: `${t('gameNotification.maxBet')}`,
          haveAdditionalContent: true,
          additionalContent: `${icon}${makeFloat(gameMaxBet)}`
        });
      }
    },
    noWagering: {
      enabled: false,
      action: (messages) => {
        messages.push({
          id: 'noWagering',
          content: (
            <Trans
              i18nKey="gameNotification.notContribute"
              components={{
                1: (
                  <Link
                    onClick={closeModal}
                    to={ROUTE.GAMES.get('bonus-games')}
                    className="text-mg_link underline"
                  />
                ),
                2: <span className="text-mg_text_label" />
              }}
            />
          ),
          haveAdditionalContent: false
        });
      }
    },
    wagering: {
      enabled: false,
      action: (messages) => {
        messages.push({
          id: 'wagering',
          content: `${t('gameNotification.wagering')}`,
          haveAdditionalContent: false
        });
      }
    }
    // Commented on task FE-9019
    // freespins: {
    //   enabled: false,
    //   action: (messages) => {
    //     messages.push({
    //       id: 'freespins',
    //       content: `${t('gameNotification.freeSpins')}`,
    //       haveAdditionalContent: true,
    //       additionalContent: betAmount
    //     });
    //   }
    // }
  };

  /** PARAMS */
  const userBonusGames = bonusAccountCasino?.wager.bonus?.wageringGames || [];
  const wageringGames = bonusAccountCasino?.wager.bonus?.wageringGames;

  const gameMaxBet = game && game.maxBet;
  const currency = game?.currency ?? 'EUR';

  const icon = getCurrencyIcon(currency);
  const isBonusHasGames = userBonusGames.length > 0;
  const isHasBonusBalance = (bonusAccountCasino?.balance ?? 0) > 0;

  // const betAmount = freeSpins?.betAmount ? `${icon} ${makeFloat(freeSpins?.betAmount)}` : '';

  const isGameInBonus =
    Boolean(game?.id) && isBonusHasGames && userBonusGames.some((item) => item.id === game?.id);

  const isGameWagering = game && game.isWagering;

  const firstConditionWagering = isHasBonusBalance && isGameWagering && !isBonusHasGames;
  const secondConditionWagering = isHasBonusBalance && isBonusHasGames && isGameInBonus;

  const firstConditionNotWagering = isHasBonusBalance && !isGameWagering && !isBonusHasGames;
  const secondConditionNotWagering = isHasBonusBalance && isBonusHasGames && !isGameInBonus;
  const gamesInBonusGames = userBonusGames?.some((game) => game.id.toString() === gameId);

  const maxBetCondition =
    game?.isWagering &&
    gameMaxBet &&
    gameMaxBet > 0 &&
    (!wageringGames || (userBonusGames && gamesInBonusGames));

  // Commented on task FE-9019
  // const freeSpinsCondition =
  //   (game?.freeSpins &&
  //     freeSpins.betAmount > 0 &&
  //     !demo &&
  //     freeSpins.number > 0 &&
  //     freeSpins.dayLimit > 0 &&
  //     freeSpins.numberDay > 0) ||
  //   (freeSpins?.number > 0 && freeSpins?.dayLimit === 0);

  /** CONDITIONS */
  // A
  if (demo) {
    notificationsConfig.deposit.enabled = true;
  }

  // B
  if (maxBetCondition) {
    notificationsConfig.maxBet.enabled = true;
  }

  // C
  if ((firstConditionWagering || secondConditionWagering) && !demo) {
    notificationsConfig.wagering.enabled = true;
  }

  if ((firstConditionNotWagering || secondConditionNotWagering) && !demo) {
    notificationsConfig.noWagering.enabled = true;
  }

  // Commented on task FE-9019
  // // D
  // if (freeSpinsCondition) {
  //   notificationsConfig.freespins.enabled = true;
  // }

  /** FINAL EFFECT */
  useEffect(() => {
    if (isAuthenticated && (!isFullBalanceReady || !isGameReady || !isShortBalanceReady)) return;
    if (!isAuthenticated && !isGameReady) return;

    const messages: GameNotificationMessage[] = [];

    Object.keys(notificationsConfig).forEach((notificationKey) => {
      const key = notificationKey as NotificationKey;
      const config = notificationsConfig[key];
      if (config.enabled) {
        config.action(messages);
      }
    });

    if (messages.length > 0) {
      openModal('GAME_NOTIFICATION', {
        props: { messages: messages }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isFullBalanceReady, isGameReady, isShortBalanceReady]);

  // Очищает возможные оставшиеся таймауты
  useEffect(() => {
    return () => {
      if (depositModalTimeout.current) clearTimeout(depositModalTimeout.current);
    };
  }, []);
}
